import { useEffect, useMemo, useRef, useState } from 'react';
import type { DesenhoRevisaoView, DesenhosDoMaterialView } from '@pieltec/contracts';
import { AlertTriangle, BadgeCheck, Eye, FilePen, FileUp, Loader2, Send, ShieldAlert } from 'lucide-react';
import { FormDialog, FormDialogBody, FormDialogContent, FormDialogDescription, FormDialogFooter, FormDialogHeader, FormDialogTitle } from '../ui/form-dialog';
import { useToast } from '../ui/toast';
import { desenhosApi, ErroDesenho, lerArquivoComoBase64, type ArquivoParaEnvio } from '../../lib/rastreamento/desenhos-api';
import { obterSessaoAtual } from '../../lib/session';

export type EstadoDosDesenhos = 'carregando' | 'erro' | 'vazio' | 'parcial' | 'pronto';

export function estadoDosDesenhos(input: { readonly carregando: boolean; readonly erro: string | null; readonly quantidade: number; readonly parcial: boolean }): EstadoDosDesenhos {
  if (input.carregando) return 'carregando';
  if (input.erro) return 'erro';
  if (input.quantidade === 0) return 'vazio';
  return input.parcial ? 'parcial' : 'pronto';
}

export function reconciliarRevisoes<T extends { readonly id: string }>(anteriores: readonly T[], recebidas: readonly T[]): T[] {
  const porId = new Map(recebidas.map((revisao) => [revisao.id, revisao]));
  const preservadas = anteriores.map((revisao) => porId.get(revisao.id) ?? revisao);
  const ids = new Set(anteriores.map((revisao) => revisao.id));
  return [...preservadas, ...recebidas.filter((revisao) => !ids.has(revisao.id))];
}

export function podePublicarDesenho(input: { readonly autorizado: boolean; readonly codigo: string; readonly motivo: string; readonly ocupada: boolean }): boolean {
  return input.autorizado && !input.ocupada && input.codigo.trim().length > 0 && input.motivo.trim().length > 0;
}

function dataLegivel(iso: string): string {
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return 'Data não informada';
  return data.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short', timeZone: 'America/Sao_Paulo' });
}

function RotuloEstadoRevisao({ revisao, vigente }: { readonly revisao: DesenhoRevisaoView; readonly vigente: boolean }) {
  // Cor nunca é o único sinal: ícone + texto em todos os estados.
  if (vigente) return <span className="inline-flex items-center gap-2 font-semibold text-success"><BadgeCheck className="size-4" aria-hidden="true" />Vigente</span>;
  if (revisao.estado === 'RASCUNHO') return <span className="inline-flex items-center gap-2 font-semibold text-warning"><FilePen className="size-4" aria-hidden="true" />Rascunho</span>;
  return <span className="inline-flex items-center gap-2 text-muted-foreground"><BadgeCheck className="size-4" aria-hidden="true" />Publicada</span>;
}

export interface TabelaRevisoesDesenhoProps {
  readonly revisoes: readonly DesenhoRevisaoView[];
  readonly revisaoVigenteId: string | null;
  readonly selecionadaId?: string | null;
  readonly onSelecionar?: (id: string) => void;
}

export function TabelaRevisoesDesenho({ revisoes, revisaoVigenteId, selecionadaId = null, onSelecionar }: TabelaRevisoesDesenhoProps) {
  return (
    <div className="max-h-64 overflow-y-auto rounded-md border border-border">
      <table className="w-full text-left text-sm">
        <caption className="sr-only">Revisões de desenho técnico</caption>
        <thead className="sticky top-0 bg-muted text-xs text-muted-foreground">
          <tr>
            <th scope="col" className="p-2">Código</th>
            <th scope="col" className="p-2">Estado</th>
            <th scope="col" className="p-2">Arquivo</th>
            <th scope="col" className="p-2">Enviado em</th>
            <th scope="col" className="p-2">Ação</th>
          </tr>
        </thead>
        <tbody>
          {revisoes.map((revisao) => (
            <tr key={revisao.id} className={revisao.id === selecionadaId ? 'bg-muted' : ''}>
              <td className="min-w-0 p-2 tabular-nums break-words">{revisao.codigoRevisao}</td>
              <td className="p-2"><RotuloEstadoRevisao revisao={revisao} vigente={revisao.id === revisaoVigenteId} /></td>
              <td className="min-w-0 p-2 break-words">{revisao.mimeType} · {tamanhoLegivel(revisao.tamanhoBytes)}</td>
              <td className="p-2 tabular-nums">{dataLegivel(revisao.createdAt)}</td>
              <td className="p-2"><button type="button" className="min-h-11 px-2 font-semibold text-primary underline" onClick={() => onSelecionar?.(revisao.id)}>Selecionar</button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function tamanhoLegivel(bytes: number): string {
  return `${(bytes / 1024).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} KB`;
}

export interface DesenhoRevisoesDialogProps {
  readonly materialId: string;
  readonly aberto: boolean;
  readonly onAbertoChange: (aberto: boolean) => void;
  /** Uma revisão vinculada por lote vence a revisão vigente do material no preview inicial. */
  readonly revisaoContextualId?: string | null;
}

export function DesenhoRevisoesDialog({ materialId, aberto, onAbertoChange, revisaoContextualId = null }: DesenhoRevisoesDialogProps) {
  const toast = useToast();
  const [dados, setDados] = useState<DesenhosDoMaterialView | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [podePublicar, setPodePublicar] = useState(false);
  const [selecionadaId, setSelecionadaId] = useState<string | null>(revisaoContextualId);
  const [arquivo, setArquivo] = useState<ArquivoParaEnvio | null>(null);
  const [codigo, setCodigo] = useState('');
  const [motivo, setMotivo] = useState('');
  const [ocupada, setOcupada] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const inputArquivo = useRef<HTMLInputElement>(null);

  const carregar = async () => {
    setCarregando(true);
    setErro(null);
    try {
      const proximo = await desenhosApi.listar(materialId);
      setDados((anterior) => anterior ? { ...proximo, revisoes: reconciliarRevisoes(anterior.revisoes, proximo.revisoes) } : proximo);
      setSelecionadaId((atual) => atual ?? revisaoContextualId ?? proximo.revisaoVigente?.id ?? proximo.revisoes[0]?.id ?? null);
    } catch (causa) {
      setErro(causa instanceof Error ? causa.message : 'Não foi possível carregar as revisões de desenho.');
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    if (!aberto) return;
    void carregar();
    let vivo = true;
    void obterSessaoAtual().then((sessao) => {
      if (vivo) setPodePublicar(sessao.autenticado && sessao.usuario.permissions.includes('desenho:publicar'));
    });
    return () => { vivo = false; };
  }, [aberto, materialId, revisaoContextualId]);

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const selecionada = useMemo(() => dados?.revisoes.find((revisao) => revisao.id === selecionadaId) ?? null, [dados, selecionadaId]);
  const estado = estadoDosDesenhos({ carregando, erro, quantidade: dados?.revisoes.length ?? 0, parcial: dados?.estado === 'RASCUNHO' });

  const escolherArquivo = async (arquivoSelecionado: File | null) => {
    if (!arquivoSelecionado) return;
    try {
      const convertido = await lerArquivoComoBase64(arquivoSelecionado);
      if (!convertido) {
        setErro('Selecione PDF, PNG ou JPEG de até 15 MB.');
        return;
      }
      setArquivo(convertido);
      setErro(null);
    } catch (causa) {
      setErro(causa instanceof Error ? causa.message : 'Não foi possível preparar o arquivo.');
    }
  };

  const enviarRascunho = async () => {
    if (!arquivo || !podePublicar || ocupada || !codigo.trim()) return;
    setOcupada(true);
    try {
      const revisao = await desenhosApi.enviarRascunho(materialId, arquivo, codigo);
      setDados((atual) => atual ? { ...atual, estado: atual.revisaoVigente ? 'PUBLICADO' : 'RASCUNHO', revisoes: reconciliarRevisoes(atual.revisoes, [revisao]) } : atual);
      setSelecionadaId(revisao.id);
      setArquivo(null);
      setCodigo('');
      toast.success({ title: 'Rascunho de desenho enviado.' });
    } catch (causa) {
      const mensagem = causa instanceof Error ? causa.message : 'Não foi possível enviar o rascunho.';
      setErro(mensagem);
      if (causa instanceof ErroDesenho && causa.status === 409) void carregar();
    } finally {
      setOcupada(false);
    }
  };

  const publicar = async () => {
    if (!selecionada || !podePublicarDesenho({ autorizado: podePublicar, codigo: selecionada.codigoRevisao, motivo, ocupada })) return;
    setOcupada(true);
    try {
      const publicada = await desenhosApi.publicar(materialId, selecionada.id, motivo);
      setDados((atual) => atual ? { ...atual, estado: 'PUBLICADO', revisaoVigente: publicada, revisoes: reconciliarRevisoes(atual.revisoes, [publicada]) } : atual);
      setMotivo('');
      toast.success({ title: `Revisão ${publicada.codigoRevisao} publicada.` });
    } catch (causa) {
      const mensagem = causa instanceof Error ? causa.message : 'Não foi possível publicar a revisão.';
      setErro(mensagem);
      if (causa instanceof ErroDesenho && causa.status === 409) void carregar();
    } finally {
      setOcupada(false);
    }
  };

  const abrirPreview = async () => {
    if (!selecionada || ocupada) return;
    setOcupada(true);
    try {
      const arquivoSelecionado = await desenhosApi.arquivo(materialId, selecionada.id);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(arquivoSelecionado.isolatedUrl ?? URL.createObjectURL(arquivoSelecionado.blob));
    } catch (causa) {
      setErro(causa instanceof Error ? causa.message : 'Não foi possível abrir esta revisão.');
    } finally {
      setOcupada(false);
    }
  };

  return (
    <FormDialog open={aberto} onOpenChange={onAbertoChange}>
      <FormDialogContent size="wide" aria-describedby={undefined}>
        <FormDialogHeader>
          <FormDialogTitle>Revisões de desenho técnico</FormDialogTitle>
          <FormDialogDescription>Publicar cria uma nova referência vigente para futuros lotes; a revisão vinculada ao lote permanece preservada.</FormDialogDescription>
        </FormDialogHeader>
        <FormDialogBody className="space-y-4">
          {estado === 'carregando' && <div aria-busy="true" className="space-y-2"><div className="ui-skeleton h-10 w-full" /><div className="ui-skeleton h-24 w-full" /></div>}
          {erro && <div role="alert" className="rounded-md border border-danger bg-card p-4 text-sm text-danger"><p><AlertTriangle className="mr-2 inline size-4" aria-hidden="true" />{erro}</p><button type="button" className="mt-2 min-h-11 font-semibold underline" onClick={() => void carregar()}>Recarregar situação atual</button></div>}
          {estado === 'vazio' && <p className="rounded-md border border-dashed border-border p-4 text-sm text-muted-foreground">{dados?.desenhoObrigatorio ? 'Este material exige desenho publicado para liberar novas ordens.' : 'Sem desenho cadastrado.'}</p>}
          {dados?.desenhoObrigatorio && !dados.revisaoVigente && <p className="rounded-md border border-warning bg-card p-4 text-sm text-warning"><ShieldAlert className="mr-2 inline size-4" aria-hidden="true" />Desenho obrigatório sem revisão publicada: a liberação de novas ordens fica bloqueada.</p>}
          {estado === 'parcial' && <p className="text-sm text-warning">Há rascunhos sem publicação. A revisão vigente não é inferida.</p>}
          {dados && dados.revisoes.length > 0 && <TabelaRevisoesDesenho revisoes={dados.revisoes} revisaoVigenteId={dados.revisaoVigente?.id ?? null} selecionadaId={selecionadaId} onSelecionar={setSelecionadaId} />}
          {selecionada && <section className="rounded-md border border-border bg-muted p-4"><p className="text-sm font-semibold break-words">Prévia selecionada: {selecionada.codigoRevisao}</p>{revisaoContextualId === selecionada.id && <p className="text-xs text-muted-foreground">Revisão usada no contexto do lote.</p>}<button type="button" className="mt-2 inline-flex min-h-11 items-center gap-2 font-semibold text-primary underline disabled:opacity-50" onClick={() => void abrirPreview()} disabled={ocupada}><Eye className="size-4" />Ver desenho da revisão selecionada</button>{previewUrl && <iframe title={`Prévia ${selecionada.codigoRevisao}`} src={previewUrl} className="mt-4 h-72 w-full rounded border border-border bg-card" />}</section>}
          {podePublicar && dados && <section className="space-y-4 rounded-md border border-border p-4"><h3 className="font-semibold text-foreground">Enviar e publicar</h3><input ref={inputArquivo} type="file" accept="application/pdf,image/png,image/jpeg" className="sr-only" onChange={(event) => void escolherArquivo(event.target.files?.[0] ?? null)} /><button type="button" className="inline-flex min-h-11 items-center gap-2 rounded border border-border bg-card px-4 font-semibold" onClick={() => inputArquivo.current?.click()} disabled={ocupada}><FileUp className="size-4" />{arquivo ? arquivo.nome : 'Selecionar arquivo'}</button><label className="block text-sm font-semibold">Código da revisão<input value={codigo} onChange={(event) => setCodigo(event.target.value)} className="mt-1 block w-full rounded border border-border bg-card p-2" /></label><button type="button" className="inline-flex min-h-11 items-center gap-2 rounded border border-border bg-card px-4 font-semibold text-foreground disabled:opacity-50" disabled={!arquivo || !codigo.trim() || ocupada} onClick={() => void enviarRascunho()}>{ocupada && <Loader2 className="size-4 animate-spin" />}Enviar rascunho</button>{selecionada?.estado === 'RASCUNHO' && <><label className="block text-sm font-semibold">Motivo da publicação<textarea value={motivo} onChange={(event) => setMotivo(event.target.value)} className="mt-1 block min-h-24 w-full rounded border border-border bg-card p-2" /></label><button type="button" className="inline-flex min-h-11 items-center gap-2 rounded bg-primary px-4 font-semibold text-primary-foreground disabled:opacity-50" disabled={!podePublicarDesenho({ autorizado: podePublicar, codigo: selecionada.codigoRevisao, motivo, ocupada })} onClick={() => void publicar()}><Send className="size-4" />Publicar revisão selecionada</button></>}</section>}
          {!podePublicar && dados && <p className="text-sm text-muted-foreground">Sua sessão permite consultar desenhos; publicação é autorizada pelo servidor para perfis habilitados.</p>}
        </FormDialogBody>
        <FormDialogFooter><button type="button" className="min-h-11 rounded border border-border bg-card px-4 font-semibold" onClick={() => onAbertoChange(false)}>Fechar</button></FormDialogFooter>
      </FormDialogContent>
    </FormDialog>
  );
}
